// src/components/kuestion/solo/SoloExam.jsx
//
// The Solo sitting, on the existing single-user session endpoints (v3 §10:
// unchanged — full results with answers and explanations). Setup → question
// → review → results, with the question palette, flags, a per-question
// clock, and answers mirrored locally so a reload does not lose them.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useAuth } from '../../../context/AuthContext';
import { useKuestionApi } from '../useKuestionApi';
import { ThemeToggle } from '../shared/ThemeContext';
import { Loading, Alert, Mark } from '../shared/Bits';
import { FlagIcon, CheckIcon, XIcon, ChevronRight, ChevronLeft } from '../shared/Icons';
import { fmtClock } from '../shared/format';
import { kq, storageKey } from '../../../config/routes';
import useAnswerSaver, { clearSeq, SAVE_LABEL } from '../shared/useAnswerSaver';

const LETTERS = 'ABCDEFGHIJ';
const storeKey = (id) => storageKey('kuestion', `solo-sitting.${id}`);

const optionOf = (opt, i) => {
  if (opt && typeof opt === 'object') return { letter: opt.letter || LETTERS[i], text: opt.text ?? '' };
  const s = String(opt ?? '');
  const m = s.match(/^([A-Z])[.)]\s*(.*)$/s);
  return m ? { letter: m[1], text: m[2] } : { letter: LETTERS[i], text: s };
};

export default function SoloExam() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const api = useKuestionApi();
  const { holdSession, releaseSession } = useAuth();

  const [templates, setTemplates] = useState(null);
  const [phase, setPhase] = useState(templateId ? 'starting' : 'setup'); // setup | starting | question | review | submitting | results | blocked
  const [session, setSession] = useState(null);
  const [template, setTemplate] = useState(null);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(() => new Set());
  const [cur, setCur] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [qElapsed, setQElapsed] = useState(0);
  const [savedAt, setSavedAt] = useState(0);
  const [outcome, setOutcome] = useState(null);
  const [error, setError] = useState('');
  const startedAt = useRef(Date.now());
  const submitting = useRef(false);

  // Template list for the setup screen.
  useEffect(() => {
    let live = true;
    api.get(API_ENDPOINTS.KQ_TEMPLATES).then((d) => live && setTemplates(d || [])).catch((e) => { if (live) { setTemplates([]); setError(e.message); } });
    return () => { live = false; };
  }, [api]);

  // Start when a template id is in the URL.
  useEffect(() => {
    if (!templateId) { setPhase('setup'); setSession(null); return undefined; }
    let live = true;
    setPhase('starting');
    setError('');
    (async () => {
      try {
        const d = await api.postJson(API_ENDPOINTS.KQ_EXAM_START, { template_id: Number(templateId) || templateId });
        if (!live) return;
        setSession(d);
        let restored = null;
        try { restored = JSON.parse(localStorage.getItem(storeKey(templateId)) || 'null'); } catch { /* ignore */ }
        if (restored && restored.session_id === d.session_id) {
          setAnswers(restored.answers || {});
          setFlagged(new Set(restored.flagged || []));
          startedAt.current = restored.started_at || Date.now();
        } else {
          setAnswers({}); setFlagged(new Set()); startedAt.current = Date.now();
        }
        setCur(0); setQElapsed(0); setOutcome(null);
        setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
        setPhase('question');
      } catch (e) {
        if (live) { setError(e.message); setPhase('blocked'); }
      }
    })();
    return () => { live = false; };
  }, [api, templateId]);

  // Pick the template object for the header once both are known.
  useEffect(() => {
    if (templates && templateId) setTemplate(templates.find((t) => String(t.id) === String(templateId)) || null);
  }, [templates, templateId]);

  // Reading a long question generates no activity; hold the idle logout.
  const active = phase === 'question' || phase === 'review';

  // Each answer goes to the server as it is given, so the sitting survives
  // this tab. Submit still carries the whole paper.
  const sessionId = session?.session_id;
  const answerUrl = useCallback((qid) => API_ENDPOINTS.KQ_EXAM_ANSWER(sessionId, qid), [sessionId]);
  const saveState = useAnswerSaver({
    api, urlFor: sessionId != null ? answerUrl : null, sittingKey: sessionId != null ? `solo.${sessionId}` : null,
    answers, flagged, enabled: active,
  });
  useEffect(() => {
    if (!active) return undefined;
    holdSession();
    return () => releaseSession();
  }, [active, holdSession, releaseSession]);

  // Mirror progress locally.
  useEffect(() => {
    if (!session || !templateId) return;
    try { localStorage.setItem(storeKey(templateId), JSON.stringify({ session_id: session.session_id, answers, flagged: [...flagged], started_at: startedAt.current })); } catch { /* ignore */ }
  }, [session, answers, flagged, templateId]);

  const limit = Number(session?.time_limit || 0) * 60;
  const remaining = Math.max(0, limit - elapsed);

  const submit = useCallback(async () => {
    if (submitting.current || !session) return;
    submitting.current = true;
    setPhase('submitting'); setError('');
    const list = (session.questions || []).map((q) => ({ question_id: q.id, user_answer: answers[q.id] || '', is_flagged: flagged.has(q.id) }));
    const timeTaken = Math.min(limit || Infinity, Math.floor((Date.now() - startedAt.current) / 1000));
    try {
      // The server grants submit a 3-hour grace past token expiry, so a long
      // sitting that outlives the token can still be handed in.
      const res = await api.postJson(API_ENDPOINTS.KQ_EXAM_SUBMIT(session.session_id), { answers: list, time_taken: timeTaken });
      try { localStorage.removeItem(storeKey(templateId)); } catch { /* ignore */ }
      clearSeq(`solo.${session.session_id}`);
      setOutcome({ ...res, time_taken: timeTaken });
      setPhase('results');
    } catch (e) {
      setError(e.message);
      setPhase('review');
    } finally {
      submitting.current = false;
    }
  }, [api, session, answers, flagged, limit, templateId]);

  // Clocks.
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => { setElapsed((s) => s + 1); setQElapsed((s) => s + 1); }, 1000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { if (active && limit > 0 && elapsed >= limit) submit(); }, [active, limit, elapsed, submit]);

  const questions = useMemo(() => session?.questions || [], [session]);
  const q = questions[cur];
  const total = questions.length;
  const answeredCount = useMemo(() => questions.filter((x) => answers[x.id]).length, [questions, answers]);
  const jump = (i) => { setCur(Math.max(0, Math.min(total - 1, i))); setQElapsed(0); setPhase('question'); };
  const pick = (letter) => { setAnswers((a) => ({ ...a, [q.id]: letter })); setSavedAt(Date.now()); };
  const toggleFlag = () => setFlagged((s) => { const n = new Set(s); if (n.has(q.id)) n.delete(q.id); else n.add(q.id); return n; });
  const exit = () => { navigate(kq('exam')); };

  // ── setup ─────────────────────────────────────────────────────────
  if (phase === 'setup') {
    return (
      <div className="kqc-page">
        <h2 className="kqc-h1" style={{ marginBottom: 6 }}>Start a sitting</h2>
        <p className="kqc-lede" style={{ marginBottom: 22, maxWidth: '54ch' }}>Pick a template. Questions are drawn from its banks at start, and your answers are kept as you go.</p>
        {error && <Alert kind="error" onClose={() => setError('')}>{error}</Alert>}
        {templates === null ? <Loading label="Loading templates" /> : templates.length === 0 ? (
          <div className="kqc-empty">No templates yet. <button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 0, display: 'inline-flex' }} onClick={() => navigate(kq('templates'))}>Build one first &rarr;</button></div>
        ) : (
          <div className="kqc-tilegrid kqc-tilegrid--tight">
            {templates.map((t) => (
              <button type="button" key={t.id} className="kqc-tilecard" onClick={() => navigate(kq(`exam/${t.id}`))}>
                <span className="kqc-kicker">{t.exam_name || 'Exam'}</span>
                <span className="kqc-tilecard__title" style={{ fontSize: 'clamp(19px,2vw,24px)' }}>{t.template_name}</span>
                <span className="kqc-tilecard__desc">{t.total_questions} questions · {t.time_limit} {t.time_unit === 'hours' ? 'h' : 'min'} · {t.total_score} marks</span>
                <span className="kqc-tilecard__foot"><span>Start exam <span className="kqc-tilecard__arrow"><ChevronRight /></span></span></span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (phase === 'starting') return <div className="kqc-page"><Loading label="Drawing your questions" /></div>;

  if (phase === 'blocked') {
    return (
      <div className="kqc-page kqc-page--narrow">
        <h2 className="kqc-h1" style={{ marginBottom: 12 }}>This sitting can&rsquo;t start</h2>
        <Alert kind="error">{error}</Alert>
        <button type="button" className="btn btn-primary" onClick={exit}>Back to templates</button>
      </div>
    );
  }

  // ── results (self-assessment-result in the mockup) ────────────────
  if (phase === 'results' && outcome) {
    const pct = Math.round((Number(outcome.percentage) || (outcome.total ? (outcome.scored / outcome.total) * 100 : 0)) * 10) / 10;
    const pos = new Map(questions.map((x, i) => [x.id, i + 1]));
    const byId = new Map(questions.map((x) => [x.id, x]));
    // Verdict and threshold come from the server, snapshotted at start (v4 §6).
    // `null` means the paper carries no pass mark: score only, no verdict.
    const passMark = outcome.pass_percentage != null ? Number(outcome.pass_percentage) : null;
    const passed = outcome.passed != null ? Boolean(outcome.passed) : null;
    const results = outcome.results || [];
    const wrong = results.filter((r) => !r.is_correct);
    // Since 17 Sep the server returns a row for every question on the paper,
    // with user_answer "" for the ones never answered. `missing` covers an
    // older server that listed answered questions only: an empty list is a
    // paper left blank, not a perfect one.
    const missing = Math.max(0, Number(outcome.total ?? total) - results.length);
    const skipped = missing + results.filter((r) => !r.user_answer).length;
    const flaggedN = results.filter((r) => flagged.has(r.question_id)).length;
    // Domain breakdown from the questions' module tags, where the bank has them.
    const perModule = new Map();
    results.forEach((r) => {
      const m = byId.get(r.question_id)?.module;
      if (!m) return;
      const cur = perModule.get(m) || { attempted: 0, correct: 0 };
      cur.attempted += 1; if (r.is_correct) cur.correct += 1;
      perModule.set(m, cur);
    });
    const domains = [...perModule.entries()].map(([name, v]) => ({ name, pct: Math.round((v.correct / v.attempted) * 100), ...v })).sort((a, b) => a.pct - b.pct);
    const textOf = (q, letter) => {
      const opt = (q?.options || []).map(optionOf).find((o) => o.letter === letter);
      return opt ? opt.text : letter;
    };
    return (
      <div className="kqc">
        <SittingTop title={session?.exam_name || template?.template_name} kicker="Results" />
        <div className="kqc-page kqc-page--mid">
          <button type="button" className="btn btn-ghost kqc-back" onClick={() => navigate(kq())}>&larr; Dashboard</button>
          <div className="kqc-row" style={{ marginBottom: 14 }}>
            <span className="tag tag-neutral">Self-assessment</span>
            <span className="kqc-small kqc-muted">{session?.exam_name || template?.template_name} &middot; {total} questions &middot; {fmtClock(outcome.time_taken)}</span>
          </div>

          <div className="kqc-scorebox">
            <div>
              <div className="kqc-tiny" style={{ marginBottom: 10 }}>Your score</div>
              <div className="kqc-score">
                <span className="kqc-score__n">{Math.round(pct)}%</span>
                {passed !== null && <span className={`kqc-score__v${passed ? '' : ' is-low'}`}>{passed ? 'PASS' : 'FAIL'}</span>}
              </div>
              <div className="kqc-passbar">
                <i className={passed === false ? 'is-low' : ''} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
                {passMark != null && <b style={{ left: `${passMark}%` }} />}
              </div>
              <div className="kqc-small kqc-muted">
                {outcome.scored} of {outcome.total} correct.{' '}
                {passMark != null ? `The pass mark for this paper is ${passMark}%, shown in red.` : 'This paper has no pass mark, so there is no verdict — just the score.'}
              </div>
            </div>
            <div>
              <div className="kqc-tiny" style={{ marginBottom: 14 }}>Domain breakdown</div>
              {domains.length === 0 ? <div className="kqc-small kqc-muted">This bank carries no module tags, so there is no per-domain split for this paper.</div> : (
                <div style={{ display: 'grid', gap: 11 }}>
                  {domains.map((d) => (
                    <div key={d.name}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, marginBottom: 5 }}><span>{d.name}</span><strong className={`kqc-mono${d.pct < 50 ? ' is-danger' : ''}`}>{d.pct}%</strong></div>
                      <div className="kqc-bar"><i className={d.pct < 50 ? 'is-accent' : ''} style={{ width: `${d.pct}%` }} /></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="kqc-rulehead" style={{ marginBottom: 8 }}>
            <h2>Answer review</h2>
            <span className="line" />
            <span className="kqc-small kqc-muted">{wrong.filter((r) => r.user_answer).length} wrong{skipped ? ` · ${skipped} left unanswered` : ''}{flaggedN ? ` · ${flaggedN} flagged` : ''}</span>
          </div>
          <p className="kqc-small" style={{ color: 'var(--color-neutral-800)', marginBottom: 20, maxWidth: '60ch' }}>This is what self-assessment gives you and a proctored sitting never does: the correct answer and the reasoning, on every question you got wrong.</p>

          {missing > 0 && <p className="kqc-small kqc-muted" style={{ marginBottom: 16 }}>{missing === 1 ? 'The one question you left unanswered is' : `The ${missing} questions you left unanswered are`} marked wrong and not shown below.</p>}
          {wrong.length === 0 ? <div className="kqc-empty" style={{ marginBottom: 24 }}>{missing > 0 ? 'Nothing you answered was wrong.' : 'Every answer was right. Nothing to review.'}</div> : (
            <div className="kqc-review2">
              {wrong.map((r, i) => {
                const q = byId.get(r.question_id);
                return (
                  <div key={r.question_id ?? i}>
                    <div className="kqc-row" style={{ marginBottom: 12 }}>
                      <span className="kqc-kicker">Question {pos.get(r.question_id) ?? i + 1} &middot; wrong</span>
                      {q?.module && <span className="tag tag-neutral">{q.module}</span>}
                      {flagged.has(r.question_id) && <span className="tag tag-outline">Flagged</span>}
                    </div>
                    <div className="kqc-review2__q">{q?.question_text}</div>
                    <div className="kqc-review2__opts">
                      <div><b>YOURS</b><span>{r.user_answer ? textOf(q, r.user_answer) : 'Not answered'}</span></div>
                      <div className="is-correct"><b>CORRECT</b><span>{textOf(q, r.correct_answer)}</span></div>
                    </div>
                    {r.explanation && <div className="kqc-review2__why"><strong>Why</strong>{r.explanation}</div>}
                  </div>
                );
              })}
            </div>
          )}

          <div className="kqc-row">
            <button type="button" className="btn btn-primary" style={{ minHeight: 46 }} onClick={() => { try { localStorage.removeItem(storeKey(templateId)); } catch { /* ignore */ } window.location.reload(); }}>Retake this mock</button>
            <button type="button" className="btn btn-secondary" style={{ minHeight: 46 }} onClick={() => navigate(kq('progress'))}>See my progress</button>
            <button type="button" className="btn btn-secondary" style={{ minHeight: 46 }} onClick={() => navigate(kq())}><CheckIcon />Done</button>
          </div>
          <p className="kqc-small kqc-muted" style={{ marginTop: 14 }}>Retakes are unlimited on a self-assessment paper. A proctored assignment would have allowed one sitting and shown you none of the above.</p>
        </div>
      </div>
    );
  }

  // ── question / review / submitting ────────────────────────────────
  const low = limit > 0 && remaining < 300;
  const flash = Date.now() - savedAt < 1800;
  return (
    <div className="kqc kqc-runner">
      <header className="kqc-runner__head">
        <span className="kqc-topbar__mark"><Mark /></span>
        <div style={{ minWidth: 0 }}>
          <div className="kqc-header__kicker">{template?.exam_name || session?.exam_name || 'Sitting'}</div>
          <div className="kqc-header__title">{template?.template_name || session?.exam_name || 'Exam'}</div>
        </div>
        <span className="kqc-spacer" />
        {flash && <span className="kqc-saved">Answer saved</span>}
        {SAVE_LABEL[saveState] && <span className={`kqc-small kqc-savestate is-${saveState}`} role="status">{SAVE_LABEL[saveState]}</span>}
        {phase === 'question' && <span className="kqc-small kqc-muted kqc-qclock">This question {fmtClock(qElapsed)}</span>}
        {limit > 0 && <span className={`kqc-clock${low ? ' is-low' : ''}`}>{fmtClock(remaining)}</span>}
        <ThemeToggle />
        <button type="button" className="btn btn-secondary btn-sm" onClick={exit}><XIcon />Exit</button>
      </header>
      <div className="kqc-progress-line"><i style={{ width: `${total ? ((cur + 1) / total) * 100 : 0}%` }} /></div>

      {phase === 'review' || phase === 'submitting' ? (
        <div className="kqc-page" style={{ maxWidth: 900 }}>
          <h2 className="kqc-h1" style={{ marginBottom: 6 }}>Review before submit</h2>
          <p className="kqc-lede" style={{ marginBottom: 18 }}>{answeredCount} answered · {total - answeredCount} unanswered · {flagged.size} flagged.{limit > 0 ? ` Time left ${fmtClock(remaining)}.` : ''}</p>
          {error && <Alert kind="error" onClose={() => setError('')}>{error} Your answers are still here.</Alert>}
          <div className="kqc-qgrid kqc-qgrid--review" style={{ marginBottom: 18 }}>
            {questions.map((x, i) => (
              <button type="button" key={x.id} className={`${answers[x.id] ? 'is-answered' : ''}${flagged.has(x.id) ? ' is-flagged' : ''}`} onClick={() => jump(i)} aria-label={`Go to question ${i + 1}`}>{i + 1}</button>
            ))}
          </div>
          <div className="kqc-hr" />
          <div className="kqc-row">
            <button type="button" className="btn btn-secondary" style={{ minHeight: 56 }} onClick={() => setPhase('question')} disabled={phase === 'submitting'}><ChevronLeft />Keep answering</button>
            <button type="button" className="btn btn-primary" style={{ minHeight: 56, fontSize: 16 }} onClick={submit} disabled={phase === 'submitting'}><CheckIcon />{phase === 'submitting' ? 'Submitting…' : 'Submit exam'}</button>
          </div>
        </div>
      ) : (
        <div className="kqc-runner__body">
          <div className="kqc-runner__q">
            {q ? (
              <>
                <div className="kqc-row" style={{ marginBottom: 14 }}>
                  <span className="kqc-kicker">Question {String(cur + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
                  {q.module && <span className="tag tag-neutral">{q.module}</span>}
                  <button type="button" className={`kqc-flagbtn kqc-flagbtn--sm${flagged.has(q.id) ? ' is-on' : ''}`} onClick={toggleFlag} aria-pressed={flagged.has(q.id)}><FlagIcon />{flagged.has(q.id) ? 'Flagged for review' : 'Flag for review'}</button>
                </div>
                <div className="kqc-hr" style={{ background: 'var(--color-divider)', margin: '0 0 18px' }} />
                <h1>{q.question_text}</h1>
                <div className="kqc-options" role="radiogroup" aria-label={`Question ${cur + 1}`}>
                  {(q.options || []).map((opt, i) => {
                    const o = optionOf(opt, i);
                    const on = answers[q.id] === o.letter;
                    return (
                      <button type="button" key={o.letter} role="radio" aria-checked={on} className={`kqc-option${on ? ' is-selected' : ''}`} onClick={() => pick(o.letter)}>
                        <span className="kqc-option__letter">{o.letter}</span>
                        <span className="kqc-option__text">{o.text}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="kqc-runner__nav">
                  <button type="button" className="btn btn-secondary" style={{ minHeight: 52 }} disabled={cur === 0} onClick={() => jump(cur - 1)}><ChevronLeft />Previous</button>
                  <button type="button" className="btn btn-ink" style={{ minHeight: 52 }} onClick={() => (cur + 1 < total ? jump(cur + 1) : setPhase('review'))}>Next<ChevronRight /></button>
                  <span className="kqc-spacer" />
                  <button type="button" className="btn btn-primary" style={{ minHeight: 52 }} onClick={() => setPhase('review')}><CheckIcon />Review &amp; submit</button>
                </div>
              </>
            ) : <div className="kqc-empty">This paper has no questions.</div>}
          </div>

          <aside className="kqc-runner__side">
            <div className="kqc-kicker kqc-kicker--dim" style={{ marginBottom: 10 }}>Questions</div>
            <div className="kqc-qgrid">
              {questions.map((x, i) => (
                <button type="button" key={x.id} className={`${i === cur ? 'is-current' : answers[x.id] ? 'is-answered' : ''}${flagged.has(x.id) ? ' is-flagged' : ''}`} onClick={() => jump(i)} aria-label={`Go to question ${i + 1}`}>{i + 1}</button>
              ))}
            </div>
            <div className="kqc-legend">
              <div><i className="is-answered" />Answered {answeredCount}</div>
              <div><i className="is-flagged" />Flagged {flagged.size}</div>
              <div><i />Unanswered {total - answeredCount}</div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

const SittingTop = ({ title, kicker }) => (
  <header className="kqc-topbar">
    <span className="kqc-topbar__mark"><Mark /></span>
    <div style={{ minWidth: 0 }}>
      <div className="kqc-header__kicker">{kicker}</div>
      <div className="kqc-header__title">{title}</div>
    </div>
    <span className="kqc-spacer" />
    <ThemeToggle />
  </header>
);
